import { Lock, ShieldAlert, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useDiscord } from "../../context/DiscordContext";

/**
 * Wraps Survivor-only content (Rules, Guide) and shows a cinematic restricted
 * screen for anyone who isn't a confirmed Survivor.
 *
 * Bypass: admins & staff always get through so they can edit the content.
 */
export default function SurvivorGate({ label = "Content", children }) {
  const { loaded, isConnected, canViewSurvivorContent, connect, inGuild } = useDiscord();

  // Still resolving auth — render nothing rather than flashing the gate.
  if (!loaded) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div
          data-testid="survivor-gate-loading"
          className="font-mono-ui text-[10px] uppercase tracking-[0.35em] text-[#5C6B51] animate-pulse"
        >
          // Verifying clearance …
        </div>
      </div>
    );
  }

  if (canViewSurvivorContent) return children;

  return (
    <section
      data-testid="survivor-gate-restricted"
      className="relative bg-[#0D110C] min-h-[80vh] py-24 sm:py-32 border-t border-[#283524] overflow-hidden"
    >
      {/* Grain / vignette backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 20%, rgba(192,168,117,0.08), transparent 55%), radial-gradient(circle at 80% 80%, rgba(192,57,43,0.06), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-[0.08]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-3xl mx-auto px-6">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 border border-[#C0392B]/60 flex items-center justify-center bg-[#161D14]">
            <ShieldAlert size={14} className="text-[#C0392B]" />
          </span>
          <span className="label-tag text-[#C0392B]">// ACCESS DENIED / SECTOR SEALED</span>
        </div>

        <h1
          data-testid="survivor-gate-title"
          className="mt-6 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
        >
          The {label} is for
          <br />
          <span className="text-[#C0A875]">Survivors only.</span>
        </h1>

        <p className="mt-6 font-mono-ui text-sm text-[#8D9A87] leading-relaxed max-w-xl">
          This handbook is passed hand-to-hand around the fire. To read it you need
          the <span className="text-[#C0A875]">Survivor</span> role in our Discord —
          granted after your whitelist application is approved.
        </p>

        {/* Checklist */}
        <ul className="mt-10 space-y-3 font-mono-ui text-xs uppercase tracking-[0.2em]">
          <GateStep
            done={isConnected}
            label="Connect your Discord"
            testid="gate-step-connect"
          />
          <GateStep
            done={isConnected && inGuild}
            label="Join the WLOU Discord server"
            testid="gate-step-guild"
          />
          <GateStep
            done={false}
            label="Submit & get approved on the whitelist"
            testid="gate-step-whitelist"
          />
        </ul>

        {/* CTAs */}
        <div className="mt-12 flex flex-col sm:flex-row gap-3">
          {!isConnected ? (
            <button
              type="button"
              onClick={connect}
              data-testid="survivor-gate-connect-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#C0A875] text-[#0D110C] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-[#d4bb84] transition-colors"
            >
              <Lock size={14} />
              Connect Discord
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          ) : (
            <Link
              to="/apply"
              data-testid="survivor-gate-apply-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#C0A875] text-[#0D110C] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-[#d4bb84] transition-colors"
            >
              Apply for whitelist
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
          <Link
            to="/"
            data-testid="survivor-gate-home-btn"
            className="inline-flex items-center justify-center gap-2 border border-[#283524] text-[#8D9A87] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] hover:text-[#E2E4DF] hover:border-[#5C6B51] transition-colors"
          >
            Back to the outpost
          </Link>
        </div>

        <p className="mt-10 label-tag text-[10px] text-[#5C6B51]">
          // If you already have the Survivor role, refresh the page — clearance
          syncs from Discord every 30 seconds.
        </p>
      </div>
    </section>
  );
}

function GateStep({ done, label, testid }) {
  return (
    <li className="flex items-center gap-3" data-testid={testid}>
      <span
        className={`w-5 h-5 border flex items-center justify-center text-[10px] ${
          done
            ? "border-[#4ADE80] text-[#4ADE80]"
            : "border-[#5C6B51] text-[#5C6B51]"
        }`}
      >
        {done ? "✓" : "·"}
      </span>
      <span className={done ? "text-[#E2E4DF]" : "text-[#8D9A87]"}>{label}</span>
    </li>
  );
}
